import { DetailsBooksItems } from '@/app/models/detailsBooks';
import { FinancialGroup } from '@/app/models/financalGroup';
import { TransferDetailBase } from '@/app/models/transferDetail';
import { useSearchDetailsBookQuery, useUnitBookMutation } from '@/app/services/detailsBooksApi';
import { useFinancialSubGroupQuery } from '@/app/services/financialGroupApi';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import LateralDrawer from '@/components/common/LateralDrawer';
import { useFinancialGroup } from '@/context/FinancialGroup';
import { useFinancialSubGroup } from '@/context/FinancialSubGroup';
import { usePeriod } from '@/context/PeriodContext';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import GenericAutoComplete from '../../common/GenericAutoComplete';
import NewEditTransferCost from '../NewEditTransferCost';
import BooksTable from './BooksTable';
import NewEditBookDetails from './NewEditBookDetails';

interface BooksProps {
  group: FinancialGroup;
}

function Books(props: BooksProps) {
  const theme = useTheme();
  const { code } = useParams();
  const { selectedPeriod } = usePeriod();
  const { group } = props;
  const { t } = useTranslation(['books', 'common']);

  const { selectedFinancialSubGroup, setSelectedFinancialSubGroup } = useFinancialSubGroup();
  const { data: financialsubGroup } = useFinancialSubGroupQuery({ groupCode: group?.id }, { skip: !group });
  useEffect(() => {
    if (financialsubGroup && financialsubGroup.length > 0) {
      const found = financialsubGroup.find((item) => item.name === group.name) ?? financialsubGroup[0];
      setSelectedFinancialSubGroup(found);
    }
  }, [financialsubGroup, group, setSelectedFinancialSubGroup]);

  const [originBook, setOriginBook] = useState<DetailsBooksItems | null>(null);
  const [originBookSearch, setOriginBookSearch] = useState('');

  const [drawerTransfer, setDrawerTransfer] = useState(false);
  const [drawerEdit, setDrawerEdit] = useState(false);
  const [selectBooksItem, setSelectedBooksItem] = useState<DetailsBooksItems>();

  const [selectBook, setSelectedBook] = useState<number[]>([]);

  const { data: detailsBook } = useSearchDetailsBookQuery(
    {
      period: selectedPeriod?.date as string,
      project: code || '',
      subGroup: selectedFinancialSubGroup?.id as number,
      searchTerm: originBookSearch || '',
    },
    {
      skip: !selectedPeriod?.date || !selectedFinancialSubGroup,
    },
  );

  const [selectedItem, setSelectedItem] = useState<TransferDetailBase | undefined>();
  const { selectedFinancialGroup } = useFinancialGroup();
  useEffect(() => {
    if (!selectBooksItem || !selectedFinancialSubGroup || !selectedPeriod) return;

    setSelectedItem({
      id: selectBooksItem.id,
      originFinancialGroupCode: selectedFinancialGroup?.code as string,
      financialSubGroupId: selectedFinancialSubGroup.id,
      period: selectedPeriod.date,
      complement: selectBooksItem.complement,
      newDetail: {
        description: selectBooksItem.description,
        value: selectBooksItem.value,
      },
    });
  }, [selectedFinancialSubGroup, selectBooksItem, selectedPeriod, selectedFinancialGroup?.code]);

  const [unit] = useUnitBookMutation();

  const handleUnite = async () => {
    await toast.promise(
      unit({
        ids: selectBook,
        period: selectedPeriod?.date as string,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:union.success') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    setDrawerEdit(false);
    setSelectedBook([]);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Box sx={{ minWidth: theme.spacing(31), display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography component="p" variant="fieldHeader">
              {t('common:search')}
            </Typography>
            <GenericAutoComplete
              value={originBook}
              onChange={(value) => {
                setOriginBook(value);
              }}
              equalityTest={(option, value) => option?.description === value?.description}
              getOptionLabel={(option) => `${option?.description ? ` ${option?.description}` : ''}`}
              onSearch={(value) => {
                setOriginBookSearch(value);
              }}
              options={[]}
              placeholder={t('common:type.to.search') as string}
              sx={{ width: theme.spacing(80) }}
            />
          </Box>
          <Box sx={{ minWidth: theme.spacing(31) }}>
            <Typography variant="fieldHeader">
              {t('common:total.by.group')}
              <Typography>{formatterMonetaryValue(detailsBook?.totalValue as number)}</Typography>
            </Typography>
          </Box>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Stack direction="row" alignItems="center">
            <Button
              variant="primary"
              sx={{ width: theme.spacing(20) }}
              onClick={() => handleUnite()}
              disabled={selectBook.length === 0}
            >
              {t('common:unite')}
            </Button>
          </Stack>
        </Box>
      </Box>

      <BooksTable
        selectBook={selectBook}
        setSelectBook={setSelectedBook}
        setDrawerEdit={setDrawerEdit}
        setDrawerTransfer={setDrawerTransfer}
        setSelectedBookItem={setSelectedBooksItem}
        selectBooksItem={selectBooksItem as DetailsBooksItems}
        bookData={detailsBook}
      />
      <LateralDrawer open={drawerEdit} setOpen={setDrawerEdit} position="right" size="bigger">
        <NewEditBookDetails selectItem={selectBooksItem as DetailsBooksItems} setDrawerOpen={setDrawerEdit} />
      </LateralDrawer>
      <LateralDrawer open={drawerTransfer} setOpen={setDrawerTransfer} position="right" size="bigger">
        <NewEditTransferCost selectedItem={selectedItem as TransferDetailBase} setDrawerOpen={setDrawerTransfer} />
      </LateralDrawer>
    </Box>
  );
}

export default Books;
